import React, { useEffect } from "react";
import { CheckCircle, Users, AlertCircle } from "lucide-react";

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("sr-visible")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".sr").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const Eligibility = () => {
  useScrollReveal();

  const whoCanApply = [
    "Must be a resident of Igembe South Constituency",
    "Must be enrolled in a recognized secondary school, college, or university",
    "Must demonstrate genuine financial need",
    "Must have a valid admission letter or proof of current enrollment",
    "Must meet minimum academic performance standards",
    "Applicants benefiting from full scholarships may not qualify",
  ];

  const requiredDocs = [
    "Copy of Student Birth Certificate or National ID",
    "Copy of Parent/Guardian National ID",
    "Official School Admission Letter",
    "Recent Academic Report / Result Slip",
    "Current Fee Structure or Invoice",
  ];

  const notices = [
    "All documents must be clear and legible.",
    "Incomplete applications will not be processed.",
    "Providing false information will lead to automatic disqualification.",
    "Meeting eligibility requirements does not guarantee approval.",
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-6xl mx-auto px-6">

        {/* Title */}
        <div className="sr text-center mb-12" style={{ transitionDelay: "0s" }}>
          <h1 className="text-4xl font-bold mb-4">Eligibility Criteria</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Applicants must meet the following criteria and provide the required
            documentation before submitting an application.
          </p>
          <div className="w-24 h-1 bg-blue-700 mx-auto rounded-full mt-6" />
        </div>

        <div className="grid md:grid-cols-2 gap-8">

          {/* Who Can Apply */}
          <div
            className="sr group bg-white rounded-2xl shadow-md p-8 flex flex-col items-start gap-4 border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300"
            style={{ transitionDelay: "0.1s" }}
          >
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <CheckCircle size={28} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Who Can Apply</h2>
            <ul className="text-gray-700 space-y-3 w-full">
              {whoCanApply.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-4 h-4 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-blue-700 block" />
                  </span>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Required Documents */}
          <div
            className="sr group bg-white rounded-2xl shadow-md p-8 flex flex-col items-start gap-4 border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300"
            style={{ transitionDelay: "0.18s" }}
          >
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <Users size={28} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Required Documents</h2>
            <ul className="text-gray-700 space-y-3 w-full">
              {requiredDocs.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-4 h-4 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-blue-700 block" />
                  </span>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Important Notice */}
        <div
          className="sr mt-10 bg-yellow-50 border border-yellow-200 rounded-2xl p-8 flex items-start gap-5 hover:shadow-md transition-shadow duration-300"
          style={{ transitionDelay: "0.26s" }}
        >
          <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle size={24} className="text-yellow-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold mb-3">Important Notice</h2>
            <ul className="text-gray-700 space-y-2">
              {notices.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="mt-1.5 w-3 h-3 rounded-full bg-yellow-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>

      <style>{`
        .sr { opacity: 0; transform: translateY(24px); transition: opacity 0.55s ease, transform 0.55s ease; }
        .sr-visible { opacity: 1; transform: translateY(0); }
      `}</style>
    </div>
  );
};

export default Eligibility;